/* eslint-disable max-lines-per-function */
import { ReactNode } from 'react';
import { useUpdateEffect } from 'react-use';
import { Merge } from 'ts-essentials';

import { Practitioner } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { ServiceLocation, ServiceLocationQuery } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';
import { Message, MessageType, StyledFlex, StyledMargin, StyledMaxWidth } from '@dreryk/react-components';
import { useCqrsSearch } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';

import { PageLoader } from '../../shared/components/PageLoader';
import { useCqrsSearchPractitionerProvidingPrivateMedicalService } from '../../shared/queries/useCqrsSearchPractitionerProvidingPrivateMedicalServiceQuery';
import { receptionServiceTranslationKeys } from '../../translations';
import { ReceptionServiceAdvancePayment } from '../shared/form/components/advancePayment/ReceptionServiceAdvancePayment';
import { receptionServicePractitionerVisitor } from '../shared/form/components/practitioner/receptionServicePractitioner.utils';
import {
   receptionServicePaymentTypeVisitor,
   receptionServicePriceVisitor,
} from '../shared/form/components/price/receptionServicePrice.utils';
import { DEFAULT_VALUE_ESTIMATED_DURATION } from '../shared/form/components/ReceptionServiceEstimatedDurationField';
import { ReceptionServiceForm, ReceptionServiceFormBean } from '../shared/form/ReceptionServiceForm';
import {
   NotUniqueReceptionServiceNameErrorHandlerProp,
   useNotUniqueReceptionServiceNameErrorHandler,
} from '../shared/form/useNotUniqueReceptionServiceNameErrorHandler.hooks';
import { serviceLocationControllerVisitor } from '../shared/form/components/serviceLocations/serviceLocations.utils';

import { useAdvancePaymentEnabledPreference } from './hooks/useAdvancePaymentEnabledPreference.hook';

type ReceptionServiceCreateFormLoaderRenderProps = {
   practitioners: Practitioner[];
   serviceLocations: ServiceLocation[];
};

type ReceptionServiceCreateFormLoaderProps = {
   readonly children: (renderProps: ReceptionServiceCreateFormLoaderRenderProps) => JSX.Element;
};

export const ReceptionServiceCreateFormLoader = (props: ReceptionServiceCreateFormLoaderProps): JSX.Element | null => {
   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearchPractitionerProvidingPrivateMedicalService();

   const {
      data: serviceLocations,
      loading: serviceLocationsLoading,
      error: serviceLocationsError,
   } = useCqrsSearch<ServiceLocation>(() => {
      return new ServiceLocationQuery();
   });

   if (practitionersLoading || serviceLocationsLoading) {
      return <PageLoader />;
   }

   if (practitionersError || serviceLocationsError) {
      return (
         <Message
            testId={'receptionServiceCreateLoaderError'}
            type={MessageType.NEGATIVE}
            label={receptionServiceTranslationKeys.create.form.errors.loadData}
            displayIndicator
         />
      );
   }

   return props.children({
      practitioners: practitioners,
      serviceLocations: serviceLocations,
   });
};

export type ReceptionServiceCreateFormBean = ReceptionServiceFormBean;

export type ReceptionServiceCreateFormController = FormContextValues<ReceptionServiceCreateFormBean>;

type ReceptionServiceCreateFormRendererProps = Merge<
   Pick<ReceptionServiceCreateFormController, 'handleSubmit' | 'formState' | 'reset'>,
   NotUniqueReceptionServiceNameErrorHandlerProp
>;

type ReceptionServiceFormRenderer = (renderProps: ReceptionServiceCreateFormRendererProps) => ReactNode;

type ReceptionServiceCreateFormProps = {
   readonly children: ReceptionServiceFormRenderer;
   readonly practitioners: Practitioner[];
   readonly serviceLocations: ServiceLocation[];
};

export const ReceptionServiceCreateForm = (props: ReceptionServiceCreateFormProps): JSX.Element => {
   const { advancePaymentEnabledPreference } = useAdvancePaymentEnabledPreference();

   const controller = useFormController<ReceptionServiceFormBean>({
      name: null,
      freeChecked: false,
      priceRangeChecked: false,
      advancePaymentChecked: advancePaymentEnabledPreference ?? true,
      price: 1,
      additionalInformation: null,
      color: null,
      estimatedDuration: DEFAULT_VALUE_ESTIMATED_DURATION,
   });

   const bean = controller.watch();

   useUpdateEffect(() => {
      controller.trigger('price');
   }, [bean.freeChecked]);

   return (
      <>
         <ReceptionServiceForm>
            <ReceptionServiceForm.FourColumnsSpan>
               <ReceptionServiceForm.NameTextInput register={controller.register} />
            </ReceptionServiceForm.FourColumnsSpan>

            {protectFeature(
               'managingVideoConsultations',
               <ReceptionServiceForm.FourColumnsSpan>
                  <ReceptionServiceForm.VideoConsultationCheckbox register={controller.register} />
               </ReceptionServiceForm.FourColumnsSpan>
            )}

            <ReceptionServiceForm.FourColumnsSpan>
               <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
                  <StyledMaxWidth maxWidth={133}>
                     <ReceptionServiceForm.EstimatedDuration register={controller.register} />
                  </StyledMaxWidth>
                  <StyledMargin right={132}>
                     <ReceptionServiceForm.ColorSelectorGroup register={controller.register} />
                  </StyledMargin>
               </StyledFlex>
            </ReceptionServiceForm.FourColumnsSpan>

            <ReceptionServiceForm.TwoColumnsSpan>
               <StyledFlex direction={'column'} gap={'8px'} flex={'1'}>
                  {receptionServicePriceVisitor(bean)({
                     withPrice: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={133}>
                              <ReceptionServiceForm.PriceMoneyInput register={controller.register} />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox register={controller.register} />
                           </StyledMargin>
                        </StyledFlex>
                     ),
                     withPriceRange: () => (
                        <StyledFlex direction={'row'} gap={'8px'}>
                           <StyledMaxWidth maxWidth={302}>
                              <ReceptionServiceForm.PriceMoneyRangeInput register={controller.register} />
                           </StyledMaxWidth>
                           <StyledMargin top={36}>
                              <ReceptionServiceForm.PriceRangeCheckbox register={controller.register} />
                           </StyledMargin>
                        </StyledFlex>
                     ),
                  })}

                  <ReceptionServiceForm.FreeToggle register={controller.register} checked={bean.freeChecked} />

                  {receptionServicePaymentTypeVisitor(bean, {
                     notFree: () => <ReceptionServiceForm.AdvancePaymentToggle register={controller.register} />,
                  })}
               </StyledFlex>
            </ReceptionServiceForm.TwoColumnsSpan>

            <ReceptionServiceForm.FourColumnsSpan>
               {receptionServicePaymentTypeVisitor(bean, {
                  withAdvancePayment: () => (
                     <>
                        <ReceptionServiceForm.AdvancePaymentWarning />
                        <ReceptionServiceAdvancePayment
                           amount={
                              <ReceptionServiceForm.AdvancePaymentAmountMoneyInput
                                 register={controller.register}
                                 watch={controller.watch}
                              />
                           }
                        />
                     </>
                  ),
               })}
               <ReceptionServiceForm.AdditionalInformationTextarea register={controller.register} />

               {receptionServicePractitionerVisitor(props.practitioners)({
                  single: ({ practitioner }) => (
                     <ReceptionServiceForm.PractitionerTextView
                        register={controller.register}
                        defaultValue={practitioner}
                     />
                  ),
                  multiple: () => (
                     <ReceptionServiceForm.PractitionersInteractiveTable
                        register={controller.register}
                        control={controller.control}
                        readOnly={bean.freeChecked}
                        header={
                           <ReceptionServiceForm.PractitionersInteractiveTable.Selector
                              practitioners={props.practitioners}
                              register={controller.register}
                              control={controller.control}
                           />
                        }
                        bottomElement={
                           <ReceptionServiceForm.PractitionersInteractiveTable.Synchronization
                              control={controller.control}
                              getValue={controller.getValue}
                              setValue={controller.setValue}
                              trigger={controller.trigger}
                              watch={controller.watch}
                           />
                        }
                     />
                  ),
               })}
               {serviceLocationControllerVisitor(
                  controller,
                  props.serviceLocations
               )({
                  single: ({ controller, serviceLocation }) => (
                     <ReceptionServiceForm.ServiceLocationTextView
                        register={controller.register}
                        defaultValue={serviceLocation}
                     />
                  ),
                  multiple: ({ controller }) => (
                     <ReceptionServiceForm.ServiceLocationsInteractiveTable
                        register={controller.register}
                        header={
                           <ReceptionServiceForm.ServiceLocationsInteractiveTable.Selector
                              serviceLocations={props.serviceLocations}
                              register={controller.register}
                              control={controller.control}
                              setValue={controller.setValue}
                           />
                        }
                     />
                  ),
               })}
            </ReceptionServiceForm.FourColumnsSpan>
         </ReceptionServiceForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
            reset: controller.reset,
            setNotUniqueReceptionServiceNameError: useNotUniqueReceptionServiceNameErrorHandler(controller.setError),
         })}
      </>
   );
};
